/**
 * Supported UI locales: the W3Techs top ten languages by web content (September 2026) plus
 * Simplified Chinese and Arabic for user reach and right-to-left coverage. Adding a locale = add an
 * entry here and a `locales/<tag>.ts` file; the i18n completeness test enforces parity with English.
 */
export interface LocaleMeta {
  tag: string;
  /** Native name, shown in the language picker. */
  name: string;
  /** English name for tooling. */
  english: string;
  dir: "ltr" | "rtl";
  /** Hint for CJK line breaking / font stacks. */
  script: "latin" | "cyrillic" | "cjk" | "arabic";
}

export const LOCALES: readonly LocaleMeta[] = [
  { tag: "en", name: "English", english: "English", dir: "ltr", script: "latin" },
  { tag: "es", name: "Español", english: "Spanish", dir: "ltr", script: "latin" },
  { tag: "de", name: "Deutsch", english: "German", dir: "ltr", script: "latin" },
  { tag: "ja", name: "日本語", english: "Japanese", dir: "ltr", script: "cjk" },
  { tag: "fr", name: "Français", english: "French", dir: "ltr", script: "latin" },
  { tag: "pt-BR", name: "Português (Brasil)", english: "Portuguese (Brazil)", dir: "ltr", script: "latin" },
  { tag: "ru", name: "Русский", english: "Russian", dir: "ltr", script: "cyrillic" },
  { tag: "it", name: "Italiano", english: "Italian", dir: "ltr", script: "latin" },
  { tag: "nl", name: "Nederlands", english: "Dutch", dir: "ltr", script: "latin" },
  { tag: "pl", name: "Polski", english: "Polish", dir: "ltr", script: "latin" },
  { tag: "zh-Hans", name: "中文（简体）", english: "Chinese (Simplified)", dir: "ltr", script: "cjk" },
  { tag: "ar", name: "العربية", english: "Arabic", dir: "rtl", script: "arabic" },
] as const;

export type LocaleTag = (typeof LOCALES)[number]["tag"];
export const DEFAULT_LOCALE: LocaleTag = "en";
export const LOCALE_TAGS: readonly string[] = LOCALES.map((l) => l.tag);

export function isLocaleTag(x: string): x is LocaleTag {
  return LOCALE_TAGS.includes(x);
}

export function localeMeta(tag: string): LocaleMeta {
  return LOCALES.find((l) => l.tag === tag) ?? LOCALES[0]!;
}

/**
 * Pick the best supported locale from a list of preferences such as `navigator.languages` or a
 * parsed `Accept-Language` header. Matches `pt-BR` → `pt`, `zh-Hans-CN` → `zh`.
 */
export function negotiateLocale(
  preferences: readonly string[],
  fallback: LocaleTag = DEFAULT_LOCALE,
): LocaleTag {
  for (const pref of preferences) {
    const lower = pref.toLowerCase().trim();
    if (!lower) continue;
    const exact = LOCALES.find((l) => l.tag.toLowerCase() === lower);
    if (exact) return exact.tag as LocaleTag;
    const base = lower.split(/[-_]/)[0] ?? "";
    const byBase = LOCALES.find((l) => l.tag.toLowerCase().split("-")[0] === base);
    if (byBase) return byBase.tag as LocaleTag;
  }
  return fallback;
}

/** Parse an `Accept-Language` header into tags ordered by quality. */
export function parseAcceptLanguage(header: string | null | undefined): string[] {
  if (!header) return [];
  return header
    .split(",")
    .map((part) => {
      const [tag, ...params] = part.trim().split(";");
      const q = params.map((p) => p.trim()).find((p) => p.startsWith("q="));
      return { tag: (tag ?? "").trim(), q: q ? Number.parseFloat(q.slice(2)) || 0 : 1 };
    })
    .filter((p) => p.tag && p.tag !== "*")
    .sort((a, b) => b.q - a.q)
    .map((p) => p.tag);
}
